// ProductNewsArticle — THÂN một bài Tin mới: ảnh bìa, chủ đề + ngày, tiêu đề, đoạn mở, nội dung,
// nút hành động. Chỉ vẽ, không gọi API — dùng chung cho khung đọc (popup), trang /whats-new/:id
// và ô XEM TRƯỚC ở trang soạn bài của Core (người soạn thấy đúng thứ khách sẽ thấy).
//
// `news` có thể là bản tóm tắt (dòng ở chuông: chưa có content_html) — khi đó vẽ phần đầu và
// một khung chờ cho nội dung, đỡ cảnh popup trắng trong lúc tải.
import { ArrowRight, BookOpen, Loader2 } from 'lucide-react';
import RichContentView from '../common/RichContentView';
import { kindMeta } from './kinds';
import { fmtDateTime } from '../../i18n/format.js';

import { useLingui } from '@lingui/react/macro';

// Máy chủ đã kiểm lúc lưu, nhưng khung này vẽ cho KHÁCH của hệ khác ⇒ gác lại ở chỗ vẽ:
// link hướng dẫn chỉ http(s) (chặn `javascript:`), đường mở tính năng chỉ là path nội bộ.
const isWebUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u.trim());
const isAppPath = (p) => typeof p === 'string' && p.startsWith('/') && !p.startsWith('//');

/**
 * @param {object}   props.news           bài (đầy đủ hoặc bản tóm tắt)
 * @param {boolean}  props.loading        đang chờ nội dung đầy đủ
 * @param {Function} props.onOpenApp      (path) ⇒ bấm nút «Dùng thử ngay» — bên bọc đóng popup + điều hướng
 * @param {Function} props.onNavigate     () ⇒ gọi NGAY TRƯỚC khi link nội bộ trong bài điều hướng
 * @param {string}   props.coverClassName lớp cho ảnh bìa (popup cho tràn mép: 'rounded-none')
 * @param {string}   props.bodyClassName  lớp cho khối chữ dưới ảnh (lề trái/phải)
 */
export default function ProductNewsArticle({ news: raw, loading = false, onOpenApp, onNavigate, coverClassName = '', bodyClassName = '' }) {
  const { t } = useLingui();
  if (!raw) return null;
  const news = {
    ...raw,
    cover_image_url: isWebUrl(raw.cover_image_url) || isAppPath(raw.cover_image_url) ? raw.cover_image_url : null,
    doc_url: isWebUrl(raw.doc_url) ? raw.doc_url.trim() : null,
    app_path: isAppPath(raw.app_path) ? raw.app_path : null,
  };
  const k = kindMeta(news.kind);
  const hasBody = typeof news.content_html === 'string';

  return (
    <article className="min-w-0">
      {news.cover_image_url ? (
        <img src={news.cover_image_url} alt=""
             className={`w-full aspect-[16/9] object-cover bg-gray-100 ${coverClassName || 'rounded-xl'}`} />
      ) : null}

      <div className={`${news.cover_image_url ? 'mt-5' : ''} ${bodyClassName}`}>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border font-medium ${k.chip}`}>{k.label}</span>
          {news.published_at ? (
            <span className="text-gray-400">{fmtDateTime(news.published_at)}</span>
          ) : (
            <span className="text-gray-400">{t`Bản nháp — chưa đăng`}</span>
          )}
        </div>

        <h1 className="mt-2 text-xl sm:text-2xl font-bold text-gray-900 leading-snug break-words">{news.title}</h1>

        {news.excerpt ? (
          <p className="mt-3 text-[15px] leading-relaxed text-gray-600 whitespace-pre-line break-words">{news.excerpt}</p>
        ) : null}

        <div className="mt-5">
          {hasBody ? (
            <RichContentView html={news.content_html} onNavigate={onNavigate} />
          ) : loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-6">
              <Loader2 size={16} className="animate-spin" /> {t`Đang tải nội dung…`}
            </div>
          ) : null}
        </div>

        {(news.app_path || news.doc_url) && (
          <div className="mt-6 pt-5 border-t border-gray-100 flex flex-wrap gap-2">
            {news.app_path ? (
              <button type="button" onClick={() => onOpenApp?.(news.app_path)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-blue-600 text-white hover:bg-blue-700">
                {news.app_label || t`Dùng thử ngay`} <ArrowRight size={16} />
              </button>
            ) : null}
            {news.doc_url ? (
              <a href={news.doc_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50">
                <BookOpen size={16} /> {news.doc_label || t`Xem hướng dẫn`}
              </a>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
}
